import { AspectRatio, VideoData } from '@lumen5/luminary';

import type { SceneDesign } from 'js/src/client';

/** Each design occupies one 512px-wide OpenAI tile in the composited strip. */
export const TILE_WIDTH = 512;
export const JPEG_QUALITY = 0.75;

/**
 * Map a loose aspect-ratio string (a Luminary `aspectRatio`, or an agent-supplied value) to the
 * `AspectRatio` enum, defaulting to landscape. Accepts "portrait" as an alias for vertical.
 */
export function normalizeAspectRatio(raw: string | undefined): AspectRatio {
    if (!raw) {
        return AspectRatio.LANDSCAPE;
    }
    if (raw === 'portrait') {
        return AspectRatio.VERTICAL;
    }
    if ((Object.values(AspectRatio) as string[]).includes(raw)) {
        return raw as AspectRatio;
    }
    return AspectRatio.LANDSCAPE;
}

/**
 * How many designs to paste side-by-side in a single strip. A 512px-wide frame
 * costs ~1 OpenAI tile when it's <=768px tall (landscape, square, fourfive) but
 * ~2 tiles once it's taller (vertical), so tall ratios use a smaller cap to keep
 * each strip around 4 tiles.
 */
const CHUNK_CAP_BY_ASPECT_RATIO: Record<AspectRatio, number> = {
    [AspectRatio.LANDSCAPE]: 4,
    [AspectRatio.SQUARE]: 4,
    [AspectRatio.FOURFIVE]: 2,
    [AspectRatio.VERTICAL]: 2,
};

export function getChunkCap(aspectRatio: AspectRatio): number {
    return CHUNK_CAP_BY_ASPECT_RATIO[aspectRatio] ?? 4;
}

/** Height in px of one 512px-wide panel for the given aspect ratio. */
export function frameHeightForAspectRatio(
    aspectRatio: AspectRatio,
    tileWidth: number = TILE_WIDTH,
): number {
    const { width, height } = VideoData.getResolution(1080, aspectRatio);
    return Math.round(tileWidth * (height / width));
}

export function chunk<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

export interface StripPlanGroup {
    groupId: number;
    groupName: string;
    /** Designs chunked into strips of at most `getChunkCap(aspectRatio)`. */
    strips: SceneDesign[][];
}

/**
 * Group designs by `display_group`, sort groups by group id, sort designs within
 * a group by `order`, then chunk each group into strips. Strips never cross a
 * group boundary. `getGroupName` resolves a display-group id to its display name
 * (empty string when unknown); a `Display Group N` fallback is applied here.
 */
export function buildStripPlan(
    designs: SceneDesign[],
    aspectRatio: AspectRatio,
    getGroupName: (groupId: number) => string,
): StripPlanGroup[] {
    const cap = getChunkCap(aspectRatio);

    const byGroup = new Map<number, SceneDesign[]>();
    for (const design of designs) {
        const existing = byGroup.get(design.display_group) ?? [];
        existing.push(design);
        byGroup.set(design.display_group, existing);
    }

    return Array.from(byGroup.keys())
        .sort((a, b) => a - b)
        .map((groupId) => {
            const groupDesigns = [...(byGroup.get(groupId) ?? [])].sort(
                (a, b) => a.order - b.order,
            );
            const resolvedName = getGroupName(groupId).trim();
            return {
                groupId,
                groupName: resolvedName || `Display Group ${groupId}`,
                strips: chunk(groupDesigns, cap),
            };
        });
}

export interface StripPanel {
    displayName: string | null;
    sceneDesignId: string;
    /** False when the offscreen render failed; the panel is still listed. */
    rendered: boolean;
}

/**
 * Build the text preamble that precedes a strip image, identifying each panel
 * left-to-right by its scene design id (plus the display name when it has one)
 * so the agent can act on a design even when its preview failed to render.
 */
export function buildStripPreamble(
    panels: StripPanel[],
    stripIndex: number,
    totalStrips: number,
): string {
    const header =
        totalStrips > 1
            ? `Strip ${stripIndex} of ${totalStrips} (panels left to right):`
            : 'Panels (left to right):';
    const lines = panels.map((panel, index) => {
        const name = panel.displayName?.trim();
        const label = name ? `${name} — ${panel.sceneDesignId}` : panel.sceneDesignId;
        const suffix = panel.rendered ? '' : ' (preview unavailable)';
        return `${index + 1}. ${label}${suffix}`;
    });
    return [header, ...lines].join('\n');
}

/**
 * Fetch an image (e.g. a saved scene design poster) as a data URL so it can be drawn
 * onto a canvas without tainting it. Resolves to `null` if it can't be fetched.
 */
export async function fetchImageAsDataUrl(url: string): Promise<string | null> {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        const blob = await response.blob();
        return await new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

/**
 * Composite N per-panel PNG data URLs into a single JPEG tile-strip: each panel
 * occupies its own 512px-wide column against a black background. A `null` entry
 * (a render that failed) keeps its column black so panel positions stay aligned
 * with the strip preamble. Browser/canvas only.
 */
export async function compositeTileStrip(
    dataUrls: (string | null)[],
    aspectRatio: AspectRatio,
): Promise<string> {
    const frameHeight = frameHeightForAspectRatio(aspectRatio);
    const canvas = document.createElement('canvas');
    canvas.width = TILE_WIDTH * dataUrls.length;
    canvas.height = frameHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not get 2d canvas context for tile strip');
    }
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < dataUrls.length; i++) {
        const dataUrl = dataUrls[i];
        if (!dataUrl) {
            continue;
        }
        const img = new Image();
        img.src = dataUrl;
        await img.decode();
        ctx.drawImage(img, i * TILE_WIDTH, 0, TILE_WIDTH, frameHeight);
    }

    const result = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    canvas.remove();
    return result;
}
